import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"
import _imports_0 from '@/assets/candidate/hall-page-background.png'
import _imports_1 from '@/assets/candidate/hall-page-fade.png'
import _imports_2 from '@/assets/candidate/hall-home-button@1.5x.png'
import _imports_3 from '@/assets/candidate/hall-results-icon@1.5x.png'
import _imports_4 from '@/assets/candidate/hall-hero-background.png'
import _imports_5 from '@/assets/candidate/hall-stat-background@1.5x.png'


const _hoisted_1 = { class: "candidate-page candidate-hall" }
const _hoisted_2 = { class: "candidate-hall__container" }
const _hoisted_3 = { class: "candidate-hall__hero" }
const _hoisted_4 = { class: "candidate-hall__stats" }
const _hoisted_5 = { class: "candidate-hall__stat" }
const _hoisted_6 = { class: "candidate-hall__stat is-warning" }
const _hoisted_7 = { class: "candidate-hall__stat is-success" }
const _hoisted_8 = { class: "candidate-hall__panel" }
const _hoisted_9 = { class: "candidate-hall__filters" }
const _hoisted_10 = { class: "candidate-hall__filter-actions" }
const _hoisted_11 = {
  class: "candidate-hall__grid",
  "aria-label": "考试场次列表"
}
const _hoisted_12 = {
  key: 1,
  class: "candidate-hall__empty"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { $enum } from '@/global/index'
import CandidateHallSessionCard from '@/components/candidate/CandidateHallSessionCard.vue'
import {
  CandidateButton,
  CandidateDateRange,
  CandidateEmpty,
  CandidateScrollbar,
  CandidateSearch,
  CandidateSelect,
} from '@/components/candidate/ui/index'
import {
  getPilotAssessmentHallPage,
  getPilotAssessmentHallSummary,
  PilotAssessmentHallPermissionStatus,
  PilotAssessmentHallSession,
  PilotAssessmentHallSummary,
} from '@/service'

type SessionStatus = 'upcoming' | 'running' | 'ended'


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter()
const keyword = ref('')
const visualStatus = ref<PilotAssessmentHallPermissionStatus>('ALL')
const visualPublishRange = ref<[Date, Date] | null>(null)
const loading = ref(true)
const errorMessage = ref('')
const enteringSessionId = ref('')
const sessions = ref<PilotAssessmentHallSession[]>([])
const hallSummary = ref<PilotAssessmentHallSummary>({
  publishedCount: 0,
  joinableCount: 0,
  myCompletedRoundCount: 0,
  personalResultAvailable: false,
})
const visualStatusOptions = [
  { label: '全部', value: 'ALL' },
  { label: '可参加', value: 'CAN_JOIN' },
  { label: '不可参加', value: 'CANNOT_JOIN' },
]
let listRequestSequence = 0

const emptyDescription = computed(() =>
  keyword.value.trim() ? '暂无匹配考试场次' : '暂无考试场次',
)

function normalizeStatus(session: PilotAssessmentHallSession): SessionStatus {
  const status = String(session.examTimeStatus || '').trim().toUpperCase()
  if (
    session.canEnter ||
    status === 'RUNNING' ||
    status === 'IN_PROGRESS' ||
    status === 'ONGOING' ||
    status === '进行中'
  ) {
    return 'running'
  }
  if (
    status === 'UPCOMING' ||
    status === 'NOT_STARTED' ||
    status === 'PENDING' ||
    status === '未开始'
  ) {
    return 'upcoming'
  }
  return 'ended'
}

function statusMeta(session: PilotAssessmentHallSession): { label: string } {
  const status = normalizeStatus(session)
  if (status === 'running') return { label: '进行中' }
  if (status === 'upcoming') return { label: '未开始' }
  return { label: '已结束' }
}

function formatLocalDate(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function goResults() {
  if (!hallSummary.value.personalResultAvailable) {
    ElMessage.info('暂无成绩')
    return
  }
  router.push('/pilot-assesment/results')
}

async function enterSession(session: PilotAssessmentHallSession) {
  if (!session.canEnter || enteringSessionId.value) return
  enteringSessionId.value = String(session.sessionId)
  try {
    await router.push({
      name: 'CandidateExam',
      params: { sessionId: String(session.sessionId) },
    })
  } finally {
    enteringSessionId.value = ''
  }
}

async function loadSessionList() {
  const requestSequence = ++listRequestSequence
  const [publishStartDate, publishEndDate] = visualPublishRange.value || []
  if (publishStartDate && publishEndDate && publishStartDate > publishEndDate) {
    throw new Error('开始发布时间不能晚于结束发布时间')
  }
  const response = await getPilotAssessmentHallPage({
    name: keyword.value.trim() || undefined,
    publishStartDate: publishStartDate ? formatLocalDate(publishStartDate) : undefined,
    publishEndDate: publishEndDate ? formatLocalDate(publishEndDate) : undefined,
    permissionStatus: visualStatus.value,
    pageNum: 1,
    pageSize: 100,
  })
  if (requestSequence !== listRequestSequence) return
  if (response.code != $enum.EnumResult.SUCCESS) {
    throw new Error(response.message || '考试场次加载失败')
  }
  sessions.value = Array.isArray(response.data && response.data.records)
    ? response.data.records
    : []
}

async function loadHall() {
  loading.value = true
  errorMessage.value = ''
  try {
    const summaryResponse = await getPilotAssessmentHallSummary({})
    if (summaryResponse.code != $enum.EnumResult.SUCCESS) {
      throw new Error(summaryResponse.message || '考试大厅统计加载失败')
    }
    hallSummary.value = {
      publishedCount: Number(summaryResponse.data?.publishedCount || 0),
      joinableCount: Number(summaryResponse.data?.joinableCount || 0),
      myCompletedRoundCount: Number(summaryResponse.data?.myCompletedRoundCount || 0),
      personalResultAvailable: Boolean(summaryResponse.data?.personalResultAvailable),
    }
    await loadSessionList()
  } catch (error) {
    sessions.value = []
    errorMessage.value = error instanceof Error ? error.message : '考试大厅加载失败'
  } finally {
    loading.value = false
  }
}

async function runSearch() {
  loading.value = true
  errorMessage.value = ''
  try {
    await loadSessionList()
  } catch (error) {
    sessions.value = []
    errorMessage.value = error instanceof Error ? error.message : '考试场次加载失败'
  } finally {
    loading.value = false
  }
}

function resetVisualFilters() {
  visualStatus.value = 'ALL'
  visualPublishRange.value = null
  keyword.value = ''
  runSearch()
}

onMounted(loadHall)

onBeforeUnmount(() => {
  listRequestSequence += 1
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _cache[15] || (_cache[15] = _createElementVNode("div", {
      class: "candidate-hall__background",
      "aria-hidden": "true"
    }, [
      _createElementVNode("img", {
        src: _imports_0,
        alt: ""
      }),
      _createElementVNode("img", {
        src: _imports_1,
        alt: ""
      })
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", { class: "candidate-hall__header candidate-page-header" }, [
        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "candidate-hall__identity" }, [
          _createElementVNode("img", {
            class: "candidate-hall__home-icon",
            src: _imports_2,
            alt: ""
          }),
          _createElementVNode("div", null, [
            _createElementVNode("h1", null, "考试大厅"),
            _createElementVNode("p", null, "查看已发布的考试场次，选择你已报名的考评进行作答")
          ])
        ], -1)),
        _createElementVNode("div", {
          class: "candidate-hall__results-button",
          onClick: goResults
        }, _cache[3] || (_cache[3] = [
          _createElementVNode("img", {
            src: _imports_3,
            alt: ""
          }, null, -1),
          _createTextVNode("   个人成绩 ")
        ]))
      ]),
      _createElementVNode("section", _hoisted_3, [
        _cache[11] || (_cache[11] = _createElementVNode("img", {
          class: "candidate-hall__hero-background",
          src: _imports_4,
          alt: ""
        }, null, -1)),
        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "candidate-hall__hero-copy" }, [
          _createElementVNode("h2", null, "选择考试场次，开始专业能力考评"),
          _createElementVNode("p", null, "系统将根据你的报名信息展示可参加的考试，请在规定时间内完成作答")
        ], -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _cache[5] || (_cache[5] = _createElementVNode("img", {
              src: _imports_5,
              alt: ""
            }, null, -1)),
            _cache[6] || (_cache[6] = _createElementVNode("span", null, "全部场次", -1)),
            _createElementVNode("strong", null, _toDisplayString(hallSummary.value.publishedCount), 1)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _cache[7] || (_cache[7] = _createElementVNode("img", {
              src: _imports_5,
              alt: ""
            }, null, -1)),
            _cache[8] || (_cache[8] = _createElementVNode("span", null, "可参加场次", -1)),
            _createElementVNode("strong", null, _toDisplayString(hallSummary.value.joinableCount), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _cache[9] || (_cache[9] = _createElementVNode("img", {
              src: _imports_5,
              alt: ""
            }, null, -1)),
            _cache[10] || (_cache[10] = _createElementVNode("span", null, "已完成轮次", -1)),
            _createElementVNode("strong", null, _toDisplayString(hallSummary.value.myCompletedRoundCount), 1)
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createVNode(_unref(CandidateSearch), {
            modelValue: keyword.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((keyword).value = $event)),
            class: "candidate-hall__search",
            placeholder: "搜索考试名称",
            clearable: "",
            "aria-label": "搜索考试名称"
          }, null, 8, ["modelValue"]),
          _createVNode(_unref(CandidateSelect), {
            modelValue: visualStatus.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((visualStatus).value = $event)),
            class: "candidate-hall__status-select",
            options: visualStatusOptions,
            "aria-label": "考试状态"
          }, null, 8, ["modelValue"]),
          _createVNode(_unref(CandidateDateRange), {
            modelValue: visualPublishRange.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((visualPublishRange).value = $event)),
            class: "candidate-hall__date-range",
            "start-placeholder": "开始发布时间",
            "end-placeholder": "结束发布时间"
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_unref(CandidateButton), {
              class: "candidate-hall__query-button",
              debounce: false,
              onClick: runSearch
            }, {
              default: _withCtx(() => _cache[13] || (_cache[13] = [
                _createTextVNode(" 查询 ")
              ])),
              _: 1
            }),
            _createVNode(_unref(CandidateButton), {
              class: "candidate-hall__reset-button",
              tone: "default",
              debounce: false,
              onClick: resetVisualFilters
            }, {
              default: _withCtx(() => _cache[14] || (_cache[14] = [
                _createTextVNode(" 重置 ")
              ])),
              _: 1
            })
          ])
        ]),
        (!loading.value && sessions.value.length)
          ? (_openBlock(), _createBlock(_unref(CandidateScrollbar), {
              key: 0,
              class: "candidate-hall__scrollbar",
              always: ""
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_11, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sessions.value, (session) => {
                    return (_openBlock(), _createBlock(CandidateHallSessionCard, {
                      key: String(session.sessionId),
                      session: session,
                      status: normalizeStatus(session),
                      "status-label": statusMeta(session).label,
                      entering: enteringSessionId.value === String(session.sessionId),
                      onEnter: enterSession
                    }, null, 8, ["session", "status", "status-label", "entering"]))
                  }), 128))
                ])
              ]),
              _: 1
            }))
          : (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createVNode(_unref(CandidateEmpty), {
                size: "large",
                description: loading.value ? '考试场次加载中...' : errorMessage.value || emptyDescription.value
              }, null, 8, ["description"])
            ]))
      ])
    ])
  ]))
}
}

})